<template>
  <transition name="toast-fade">
    <div v-if="visible" class="custom-toast" :class="`custom-toast-${type}`">
      <div class="toast-content">
        <div class="toast-icon-wrapper">
          <i class="toast-icon" :class="iconClass"></i>
        </div>
        <span class="toast-message">{{ message }}</span>
      </div>
      <i class="toast-close el-icon-close" @click="close"></i>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CustomToast',
  data() {
    return {
      visible: false,
      message: '',
      type: 'success', // success, error, warning, info
      duration: 2000,
      timer: null
    }
  },
  computed: {
    iconClass() {
      const iconMap = {
        success: 'el-icon-circle-check',
        error: 'el-icon-circle-close',
        warning: 'el-icon-warning',
        info: 'el-icon-info'
      }
      return iconMap[this.type] || iconMap.success
    }
  },
  methods: {
    show(options) {
      if (typeof options === 'string') {
        this.message = options
        this.type = 'success'
      } else {
        this.message = options.message || ''
        this.type = options.type || 'success'
        this.duration = options.duration || 2000
      }
      
      this.visible = true
      
      if (this.timer) {
        clearTimeout(this.timer)
      }
      
      this.timer = setTimeout(() => {
        this.close()
      }, this.duration)
    },
    close() {
      this.visible = false
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }
}
</script>

<style scoped>
.custom-toast {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: var(--card-bg, #fff);
  min-width: 300px;
  max-width: 450px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.toast-icon-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.toast-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.toast-message {
  font-size: 14px;
  color: var(--text-primary, #333);
  line-height: 1.5;
  word-break: break-word;
  flex: 1;
  transition: color 0.3s ease;
}

.toast-close {
  font-size: 16px;
  color: var(--text-tertiary, #909399);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s;
}

.toast-close:hover {
  color: var(--text-secondary, #606266);
}

/* Success 样式 - 使用主题色 */
.custom-toast-success {
  background: #f0f9ff;
  border: 1px solid var(--primary-color, #0156DC);
}

.custom-toast-success .toast-icon {
  color: var(--primary-color, #0156DC);
}

.custom-toast-success .toast-message {
  color: var(--text-primary, #333);
}

/* Error 样式 - 红色主题 */
.custom-toast-error {
  background: #fef0f0;
  border: 1px solid var(--danger-color, #f56c6c);
}

.custom-toast-error .toast-icon {
  color: var(--danger-color, #f56c6c);
}

.custom-toast-error .toast-message {
  color: var(--text-primary, #333);
}

/* Warning 样式 - 橙色主题 */
.custom-toast-warning {
  background: #fdf6ec;
  border: 1px solid var(--warning-color, #e6a23c);
}

.custom-toast-warning .toast-icon {
  color: var(--warning-color, #e6a23c);
}

.custom-toast-warning .toast-message {
  color: var(--text-primary, #333);
}

/* Info 样式 - 使用主题色 */
.custom-toast-info {
  background: #f0f9ff;
  border: 1px solid var(--primary-color, #0156DC);
}

.custom-toast-info .toast-icon {
  color: var(--primary-color, #0156DC);
}

.custom-toast-info .toast-message {
  color: var(--text-primary, #333);
}

/* 过渡动画 */
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.toast-fade-enter-to,
.toast-fade-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* 夜间模式适配 */
.dark-mode .custom-toast {
  background: var(--card-bg, #2c2c2c);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
}

.dark-mode .custom-toast-success {
  background: rgba(42, 123, 240, 0.2);
  border-color: var(--primary-color, #2A7BF0);
}

.dark-mode .custom-toast-success .toast-icon {
  color: var(--primary-color, #2A7BF0);
}

.dark-mode .custom-toast-error {
  background: rgba(245, 108, 108, 0.2);
  border-color: var(--danger-color, #f56c6c);
}

.dark-mode .custom-toast-error .toast-icon {
  color: var(--danger-color, #f56c6c);
}

.dark-mode .custom-toast-warning {
  background: rgba(230, 162, 60, 0.2);
  border-color: var(--warning-color, #e6a23c);
}

.dark-mode .custom-toast-warning .toast-icon {
  color: var(--warning-color, #e6a23c);
}

.dark-mode .custom-toast-info {
  background: rgba(42, 123, 240, 0.2);
  border-color: var(--primary-color, #2A7BF0);
}

.dark-mode .custom-toast-info .toast-icon {
  color: var(--primary-color, #2A7BF0);
}

.dark-mode .toast-message {
  color: var(--text-primary, #e0e0e0);
}

.dark-mode .toast-close {
  color: var(--text-tertiary, #909399);
}

.dark-mode .toast-close:hover {
  color: var(--text-secondary, #c0c4cc);
}
</style>

